import React from 'react'
import { useNavigate } from 'react-router-dom'
import PlusIcon from 'client/utils/icons/plus-icon'
import {
  DcButton,
  DcDiv,
  DcFlexBox,
  DcDropDown,
  DcInput,
  DcOverlaySlider,
  DcSwitch,
  DcText,
  PixelCurrency,
  PixelDropZone,
  PixelFactoryContext,
  PixelPageContainer,
  PixelCheckboxGroup,
  PixelSpinner,
  PixelTable,
  Section,
  SectionContent,
  SectionTitle,
} from 'pixel-base'
import { useLocalStorage } from 'shared/hooks'
import styled from 'styled-components'

import {
  addNewProducts,
  get,
  getCategories,
  getProductMeta,
  updateProducts,
} from 'utils/api-routes.util'

import AddNewCategory from './AddNewCategory'
import Variants from './Variants'
import {
  ICollections,
  IPixelFactory,
  IProductDetails,
  LinkedStore,
} from '../../../../../../decl.d'

const ProductForm = (props) => {
  const { productId, setEditId, setProductId } = props
  const navigate = useNavigate()
  const [isShowSpinner, setIsShowSpinner] = React.useState(false)

  const [variantPopupAction, setVariantPopupAction] = React.useState('edit')
  const [product, setProduct] = React.useState<IProductDetails>({
    name: '',
    description: '',
    short_description: '',
    price: null,
    sale_price: null,
    sku: '',
    weight: '',
    category_name: null,
    category_id: null,
    cost_price: null,
    is_sellable: 1,
    barcode: '',
    product_id: null,
    stock: null,
    images: [],
    publish_on: [],
    manage_inventory: 0,
  })
  const { showSuccess, showFailure } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const productformsref = React.useRef(null)
  const [categories, setCategories] = React.useState<ICollections>([])
  const [stores, setStores] = React.useState<LinkedStore>([])
  const [error, setError] = React.useState({
    error: '',
    field: '',
  })

  const [tenant_info] = useLocalStorage('tenant_info', {
    default_currency_symbol: 'PKR',
  })

  React.useEffect(() => {
    if (!productId) {
      setProduct({
        name: '',
        description: '',
        short_description: '',
        price: null,
        sale_price: null,
        sku: '',
        weight: '',
        category_name: null,
        category_id: null,
        cost_price: null,
        is_sellable: 1,
        barcode: '',
        product_id: null,
        stock: null,
        images: [],
        publish_on: [],
        manage_inventory: 0,
      })
    }
  }, [productId])

  const [showAddNewCategoryPopup, setShowAddNewCategoryPopup] =
    React.useState(false)
  const [showAddNewVariantPopup, setShowAddNewVariantPopup] =
    React.useState(false)
  const fetchProductsMeta = () => {
    setIsShowSpinner(true)
    getProductMeta().subscribe((res) => {
      const response = res.response as {
        linked_stores: LinkedStore[]
      }
      setIsShowSpinner(false)
      const store = response?.linked_stores?.map((item) => {
        return {
          ...item,
          active: item.store_type === 'ems',
        }
      })
      setStores(store)
      fetchProductCategories()
    })
    setIsShowSpinner(false)
  }
  const fetchProductDetails = () => {
    setIsShowSpinner(true)
    get(`/products/${productId}`).subscribe((res) => {
      const response = res.response as { data: IProductDetails }
      setIsShowSpinner(false)
      setProduct({
        name: response?.data?.product_name,
        description: response?.data?.product_description,
        short_description: response?.data?.short_description,
        price: response?.data?.price,
        sale_price: response?.data?.sale_price,
        sku: response?.data?.product_sku,
        weight: response?.data?.weight,
        category_name: response?.data?.category_name,
        category_id: response?.data?.category_id,
        cost_price: response?.data?.cost_price,
        is_sellable: response?.data?.is_sellable === true ? 1 : 0,
        barcode: response?.data?.barcode,
        product_id: response?.data?.product_id,
        stock: response?.data?.quantity_available,
        images: response?.data?.images,
        publish_on: response?.data?.publish_on,
        manage_inventory: response?.data?.manage_inventory,
        inventory_distribution: response?.data?.inventory_distribution,
        variations: response?.data?.variations,
      })
    })
  }
  const fetchProductCategories = () => {
    getCategories().subscribe((res) => {
      const response = res.response as { data: ICollections }
      setCategories(response?.data)
      if (response?.data.length !== 0) {
        const value = response?.data[0]['id']
        if (!productId) {
          setProduct({
            ...product,
            category_id: value,
          })
        }
      }
    })
  }

  const addProduct = () => {
    const storesIds = stores
      .filter((item) => item.active)
      .map((item) => item.store_id)
    setProduct({
      ...product,
      publish_on: storesIds,
    })
    const formdata = new FormData()
    Object.keys(product).map((key) => {
      if (key !== 'images') {
        formdata.append(key, product[key])
      } else {
        ;[...product['images']].map((singleFile: File) => {
          delete singleFile.preview
          formdata.append('images[]', singleFile, singleFile.name)
        })
      }
    })
    if (product.manage_inventory === 0) {
      formdata.delete('stock')
      formdata.delete('inventory_distribution')
      formdata.delete('variations')
    }
    addNewProducts(formdata).subscribe((res) => {
      const response = res.response as { error: string; field: string }
      if (response.error) {
        showFailure(response.error)
        setError({
          error: response.error,
          field: response.field,
        })
      } else {
        showSuccess('Product added successfully')
        navigate('/inventory/products')
      }
    })
  }

  const handleUpdateProduct = () => {
    setIsShowSpinner(true)
    if (product.cost_price) {
      product.cost_price = Number(product.cost_price)
    }

    updateProducts(product).subscribe((res) => {
      setIsShowSpinner(false)
      const response = res.response as {
        error: string
        field: string
        data: IProductDetails
      }
      if (response?.error) {
        showFailure(response.error)
        setError({
          error: response.error,
          field: response.field,
        })
      } else if (response?.data) {
        showSuccess('Product updated successfully')
        setEditId(null)
        setProductId(productId)
      } else {
        showFailure('Something went wrong')
      }
    })
  }
  React.useEffect(() => {
    fetchProductsMeta()
  }, [])
  React.useEffect(() => {
    if (productId) {
      fetchProductDetails()
    }
  }, [productId])
  const handleSubmit = () => {
    addProduct()
  }

  const breadcrumbs = [
    {
      name: 'All Products',
      link: '/inventory/products',
    },
    {
      name: `${productId ? 'Edit' : 'Add'} Product`,
      active: true,
    },
  ]

  if (productId) {
    breadcrumbs.splice(1, 0, {
      name: 'Product Details',
      link: `/inventory/products/detail/${productId}`,
    })
  }

  const handleSwitchChange = (e, i) => {
    const newStores = [...stores]
    newStores[i].active = e.target.checked
    setStores(newStores)
  }
  const handleNegativePrice = (e) => {
    if (e.key === '-') {
      e.preventDefault()
      showFailure('Price can not be Negative')
    }
  }
  const columns = [
    {
      dataKey: 'variation_sku',
      name: 'Variant SKU',
      width: '20%',
      sortable: true,
      selector: (row) => (row.variation_sku ? row.variation_sku : '--'),
    },

    {
      dataKey: 'price',
      name: `Price (${tenant_info.default_currency_symbol})`,
      sortable: true,
      selector: (row) =>
        row.price ? (
          <PixelCurrency hideSymbol={true} value={row.price} />
        ) : (
          '--'
        ),
    },
    {
      dataKey: 'cost_price',
      name: `Cost Price (${tenant_info.default_currency_symbol})`,
      sortable: true,
      selector: (row) =>
        row.cost_price ? (
          <PixelCurrency hideSymbol={true} value={row.cost_price} />
        ) : (
          '--'
        ),
    },
    {
      dataKey: 'sold_count',
      name: 'Sold',
      sortable: true,
      selector: (row) => (row.sold_count ? row.sold_count : '--'),
    },
    {
      dataKey: 'quantity_available',
      name: 'Stock',
      sortable: true,
      selector: (row) =>
        row.quantity_available ? row.quantity_available : '0',
    },

    {
      dataKey: 'weight',
      name: 'Weight',
      sortable: true,
      selector: (row) => (row.weight ? row.weight : '--'),
    },
  ]

  return (
    <>
      <PixelSpinner isShow={isShowSpinner} />

      <PixelPageContainer
        padding="20px"
        height={'calc(100vh - 65px)'}
        className="Product-Form"
        ref={productformsref}
      >
        <DcFlexBox flexDirection="column" gap="20px">
          <Section style={{ padding: '0px' }}>
            <SectionContent>
              <SectionTitle>Product Info</SectionTitle>

              <FormGroup>
                <DcDiv
                  width="100%"
                  height="auto"
                  style={{
                    display: 'flex',

                    flexDirection: 'column',
                    alignItems: 'flexStart',
                  }}
                >
                  <DcText
                    style={{
                      textAlign: 'left',

                      color: '#2e314a',
                      fontSize: '.875rem',
                      fontWeight: '500',
                      marginBottom: '.5rem',
                    }}
                  >
                    Product Name
                  </DcText>
                  <DcInput
                    error={
                      error.field === 'name' && error.error !== ''
                        ? error.error
                        : null
                    }
                    required
                    type={'text'}
                    name={'Product Name'}
                    placeholder={'Product Name'}
                    value={product.name}
                    onChange={(e) =>
                      setProduct({ ...product, name: e.target.value })
                    }
                  />
                </DcDiv>
              </FormGroup>

              <FormGroup>
                <DcDiv
                  width="100%"
                  height="auto"
                  style={{
                    display: 'flex',

                    flexDirection: 'column',
                    alignItems: 'flexStart',
                  }}
                >
                  <DcText
                    style={{
                      textAlign: 'left',

                      color: '#2e314a',
                      fontSize: '.875rem',
                      fontWeight: '500',
                      marginBottom: '.5rem',
                    }}
                  >
                    Description
                  </DcText>
                  <DcInput
                    error={
                      error.field === 'description' && error.error !== ''
                        ? error.error
                        : null
                    }
                    required
                    type={'text'}
                    as={'textarea'}
                    name={'Product Description'}
                    placeholder={'Product Description'}
                    value={product.description}
                    onChange={(e) =>
                      setProduct({ ...product, description: e.target.value })
                    }
                  />
                </DcDiv>
              </FormGroup>
            </SectionContent>
          </Section>
          <Section style={{ padding: '0px' }}>
            <SectionContent>
              <SectionTitle>Details</SectionTitle>
              <FormGroup>
                <DcDiv
                  width="100%"
                  height="auto"
                  style={{
                    display: 'flex',

                    flexDirection: 'column',
                    alignItems: 'flexStart',
                  }}
                >
                  <DcText
                    style={{
                      textAlign: 'left',

                      color: '#2e314a',
                      fontSize: '.875rem',
                      fontWeight: '500',
                      marginBottom: '.5rem',
                    }}
                  >
                    Category
                  </DcText>
                  <DcFlexBox gap="10px" height="auto" width="100%">
                    <DcDropDown
                      error={
                        error.field === 'category_id' && error.error !== ''
                          ? error.error
                          : null
                      }
                      options={[
                        { label: 'Select Collections', value: '' },
                        ...categories.map((data: ICollections) => {
                          return {
                            label: data.collection_id?.source_collection_name,
                            value: data.collection_id?.source_collection_id,
                          }
                        }),
                      ]}
                      placeholder={'Select Collection'}
                      name={'Product Collections'}
                      value={product.category_id}
                      onChange={(e) =>
                        setProduct({ ...product, category_id: e.target.value })
                      }
                    />
                    <DcButton
                      onClick={() => {
                        setShowAddNewCategoryPopup(true)
                      }}
                      variant={'outline-info'}
                    >
                      <PlusIcon size={16} />
                    </DcButton>
                  </DcFlexBox>
                </DcDiv>
              </FormGroup>
              <FormGroup>
                <AllowSalesContainer>
                  <SwitchDescription>Allow Sales?</SwitchDescription>
                  <DcSwitch
                    // className={"large"}
                    value={product.is_sellable === 1 ? true : false}
                    onChange={(e) => {
                      setProduct({
                        ...product,
                        is_sellable: e.target.checked === true ? 1 : 0,
                      })
                    }}
                    checked={product.is_sellable === 1 ? true : false}
                    label={product.is_sellable === 1 ? 'on' : 'off'}
                  />{' '}
                </AllowSalesContainer>
              </FormGroup>
            </SectionContent>
          </Section>
          <Section style={{ padding: '0px' }}>
            <SectionContent>
              <SectionTitle>Inventory</SectionTitle>
              <FormGroup>
                <DcDiv
                  width="100%"
                  height="auto"
                  style={{
                    display: 'flex',

                    flexDirection: 'column',
                    alignItems: 'flexStart',
                  }}
                >
                  <DcText
                    style={{
                      textAlign: 'left',

                      color: '#2e314a',
                      fontSize: '.875rem',
                      fontWeight: '500',
                      marginBottom: '.5rem',
                    }}
                  >
                    Product SKU
                  </DcText>
                  <DcInput
                    disabled={!!productId}
                    error={
                      error.field === 'sku' && error.error !== ''
                        ? error.error
                        : null
                    }
                    required
                    type={'text'}
                    name={'Product SKU'}
                    placeholder={'Product SKU'}
                    value={product.sku}
                    onChange={(e) =>
                      setProduct({ ...product, sku: e.target.value })
                    }
                  />
                </DcDiv>
                <DcDiv
                  width="100%"
                  height="auto"
                  style={{
                    display: 'flex',

                    flexDirection: 'column',
                    alignItems: 'flexStart',
                  }}
                >
                  <DcText
                    style={{
                      textAlign: 'left',

                      color: '#2e314a',
                      fontSize: '.875rem',
                      fontWeight: '500',
                      marginBottom: '.5rem',
                    }}
                  >
                    Barcode
                  </DcText>
                  <DcInput
                    error={
                      error.field === 'barcode' && error.error !== ''
                        ? error.error
                        : null
                    }
                    type={'text'}
                    name={'Barcode'}
                    placeholder={'Barcode'}
                    value={product.barcode}
                    onChange={(e) =>
                      setProduct({ ...product, barcode: e.target.value })
                    }
                  />
                </DcDiv>
              </FormGroup>
              {productId === null && (
                <>
                  <PixelCheckboxGroup
                    options={[
                      {
                        label: 'Manage Inventory',
                        value: true,
                        type: 'checkbox',
                        checked: product.manage_inventory === 0 ? false : true,
                      },
                    ]}
                    onChange={(e) => {
                      setProduct({
                        ...product,
                        manage_inventory: e.target.checked === true ? 1 : 0,
                        type: e.target.checked === true && 'variable',
                      })
                    }}
                  />
                  <FormGroup>
                    {product.manage_inventory === 1 && (
                      <>
                        <DcDiv
                          width="100%"
                          height="auto"
                          style={{
                            display: 'flex',

                            flexDirection: 'column',
                            alignItems: 'flexStart',
                          }}
                        >
                          <DcText
                            style={{
                              textAlign: 'left',

                              color: '#2e314a',
                              fontSize: '.875rem',
                              fontWeight: '500',
                              marginBottom: '.5rem',
                            }}
                          >
                            Fist Name
                          </DcText>
                        </DcDiv>
                        <DcDiv
                          width="100%"
                          height="auto"
                          style={{
                            display: 'flex',

                            flexDirection: 'column',
                            alignItems: 'flexStart',
                          }}
                        >
                          <DcText
                            style={{
                              textAlign: 'left',

                              color: '#2e314a',
                              fontSize: '.875rem',
                              fontWeight: '500',
                              marginBottom: '.5rem',
                            }}
                          >
                            Fist Name
                          </DcText>
                          <DcInput
                            disabled={!!productId}
                            error={
                              error.field === 'stock' && error.error !== ''
                                ? error.error
                                : null
                            }
                            required
                            type={'number'}
                            name={'Quantity Available'}
                            placeholder={'Quantity Available'}
                            value={product.stock}
                            onChange={(e) =>
                              setProduct({ ...product, stock: e.target.value })
                            }
                          />
                        </DcDiv>
                      </>
                    )}
                  </FormGroup>
                </>
              )}
            </SectionContent>
          </Section>
          {productId && (
            <>
              {product?.variations.length > 0 && (
                <Section style={{ padding: '0px' }}>
                  <SectionContent>
                    <SectionContainer>
                      {' '}
                      <SectionTitle style={{ padding: '0px' }}>
                        Variations
                      </SectionTitle>
                      <DcButton
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          setShowAddNewVariantPopup(true)
                          setVariantPopupAction('new')
                        }}
                      >
                        Add New
                      </DcButton>
                    </SectionContainer>

                    <PixelTable
                      columns={columns}
                      data={product?.variations}
                      pagination={
                        product?.variations?.length > 25 ? true : false
                      }
                    />

                    <FormGroup></FormGroup>
                  </SectionContent>
                </Section>
              )}
            </>
          )}

          <Section style={{ padding: '0px' }}>
            <SectionContent>
              <SectionTitle>Pricing</SectionTitle>
              <FormGroup>
                <DcDiv
                  width="100%"
                  height="auto"
                  style={{
                    display: 'flex',

                    flexDirection: 'column',
                    alignItems: 'flexStart',
                  }}
                >
                  <DcText
                    style={{
                      textAlign: 'left',

                      color: '#2e314a',
                      fontSize: '.875rem',
                      fontWeight: '500',
                      marginBottom: '.5rem',
                    }}
                  >
                    Price
                  </DcText>
                  <DcInput
                    error={
                      error.field === 'price' && error.error !== ''
                        ? error.error
                        : null
                    }
                    required
                    min={0}
                    type={'number'}
                    name={'Price'}
                    placeholder={'Price'}
                    value={product.price}
                    onKeyDown={handleNegativePrice}
                    onChange={(e) => {
                      setProduct({
                        ...product,
                        price: e.target.value,
                      })
                    }}
                  />
                </DcDiv>
                <DcDiv
                  width="100%"
                  height="auto"
                  style={{
                    display: 'flex',

                    flexDirection: 'column',
                    alignItems: 'flexStart',
                  }}
                >
                  <DcText
                    style={{
                      textAlign: 'left',

                      color: '#2e314a',
                      fontSize: '.875rem',
                      fontWeight: '500',
                      marginBottom: '.5rem',
                    }}
                  >
                    Sale Price
                  </DcText>
                  <DcInput
                    error={
                      error.field === 'sale_price' && error.error !== ''
                        ? error.error
                        : null
                    }
                    min={0}
                    required
                    type={'number'}
                    name={'Sale Price'}
                    placeholder={'Sale Price'}
                    value={product.sale_price}
                    onKeyDown={handleNegativePrice}
                    onChange={(e) =>
                      setProduct({ ...product, sale_price: e.target.value })
                    }
                  />
                </DcDiv>
              </FormGroup>
              <FormGroup>
                <DcDiv
                  width="100%"
                  height="auto"
                  style={{
                    display: 'flex',

                    flexDirection: 'column',
                    alignItems: 'flexStart',
                  }}
                >
                  <DcText
                    style={{
                      textAlign: 'left',

                      color: '#2e314a',
                      fontSize: '.875rem',
                      fontWeight: '500',
                      marginBottom: '.5rem',
                    }}
                  >
                    Cost Price
                  </DcText>
                  <DcInput
                    error={
                      error.field === 'cost_price' && error.error !== ''
                        ? error.error
                        : null
                    }
                    type={'number'}
                    min={0}
                    name={'Cost Price'}
                    placeholder={'Cost Price'}
                    value={product.cost_price}
                    onKeyDown={handleNegativePrice}
                    onChange={(e) => {
                      setProduct({
                        ...product,
                        cost_price: e.target.value,
                      })
                    }}
                  />
                </DcDiv>
                <DcDiv
                  width="100%"
                  height="auto"
                  style={{
                    display: 'flex',

                    flexDirection: 'column',
                    alignItems: 'flexStart',
                  }}
                >
                  <DcText
                    style={{
                      textAlign: 'left',

                      color: '#2e314a',
                      fontSize: '.875rem',
                      fontWeight: '500',
                      marginBottom: '.5rem',
                    }}
                  >
                    Weight
                  </DcText>
                  <DcInput
                    error={
                      error.field === 'weight' && error.error !== ''
                        ? error.error
                        : null
                    }
                    type={'number'}
                    min={0}
                    name={'Weight'}
                    placeholder={'Weight'}
                    value={product.weight}
                    onChange={(e) =>
                      setProduct({ ...product, weight: e.target.value })
                    }
                  />
                </DcDiv>
              </FormGroup>
            </SectionContent>
          </Section>
          {productId ? (
            ''
          ) : (
            <Section style={{ padding: '0px' }}>
              <SectionContent>
                <SectionTitle>Image</SectionTitle>
                <PixelDropZone
                  onChange={(files) => {
                    setProduct({ ...product, images: files })
                  }}
                />
              </SectionContent>
            </Section>
          )}
          <Section style={{ padding: '0px' }}>
            <SectionContent>
              <SectionTitle>Publish on stores</SectionTitle>

              <SwitchContainer>
                {stores?.map((store: LinkedStore, i) => {
                  return (
                    <SectionContainer key={i}>
                      <SwitchDescription>{store.store_name}</SwitchDescription>
                      <DcSwitch
                        disabled={store.store_type === 'ems' ? true : false}
                        value={store.active}
                        onChange={(e) => {
                          handleSwitchChange(e, i)
                        }}
                        checked={store.active}
                        label={store.active ? 'on' : 'off'}
                      />
                    </SectionContainer>
                  )
                })}
              </SwitchContainer>
            </SectionContent>
          </Section>
          <Section style={{ padding: '0px' }}>
            <SectionContent>
              <FormGroup flexEnd>
                <DcButton
                  disabled={
                    productId
                      ? false
                      : product.name === '' ||
                        product.description === '' ||
                        product.category_id === '' ||
                        product.sku === '' ||
                        product.stock === '' ||
                        product.price === '' ||
                        product.sale_price === '' ||
                        product.cost_price === '' ||
                        product.weight === ''
                  }
                  onClick={() => {
                    if (productId) {
                      handleUpdateProduct()
                    } else {
                      handleSubmit()
                    }
                  }}
                  variant="primary"
                >
                  {productId ? 'Update' : 'Save'}
                </DcButton>
              </FormGroup>
            </SectionContent>
          </Section>
        </DcFlexBox>
        {showAddNewCategoryPopup && (
          <DcOverlaySlider
            title={'Add Product Category'}
            isOpen={showAddNewCategoryPopup}
            onClose={() => setShowAddNewCategoryPopup(false)}
            width="50%"
          >
            {showAddNewCategoryPopup && (
              <SectionContent>
                <AddNewCategory
                  categories={categories}
                  handleClose={(data: ICollections | null) => {
                    setShowAddNewCategoryPopup(false)
                    if (data) {
                      setCategories([data, ...categories])
                      setProduct({
                        ...product,
                        category: data.collection_id.source_collection_id,
                      })
                    }
                  }}
                />
              </SectionContent>
            )}
          </DcOverlaySlider>
        )}
        {showAddNewVariantPopup && (
          <DcOverlaySlider
            width="50%"
            title={
              variantPopupAction === 'new'
                ? 'Add New Variation'
                : 'Edit Variation'
            }
            isOpen={showAddNewVariantPopup}
            onClose={() => setShowAddNewVariantPopup(false)}
          >
            {showAddNewVariantPopup && (
              <SectionContent>
                <Variants
                  action={variantPopupAction}
                  item={{}}
                  product_id={productId}
                  product={product}
                  handleClose={(data: {
                    variation_sku: string
                    price: number
                    cost_price: number
                    sold_count: number
                    quantity_available: number
                  }) => {
                    if (data) {
                      setProduct({
                        ...product,
                        variations: [...(product?.variations || {}), data],
                      })
                    }
                    setShowAddNewVariantPopup(false)
                  }}
                />
              </SectionContent>
            )}
          </DcOverlaySlider>
        )}
      </PixelPageContainer>
    </>
  )
}

const SwitchContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;
`

const FormGroup = styled.div<{ flexEnd?: boolean }>`
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: ${(props) => (props.flexEnd ? 'flex-end' : 'center')};
  align-items: center;
  align-content: flex-start;
  width: 100%;
  flex-grow: 1;
  gap: 10px;
  .full-width {
    flex-grow: 1;
  }
  margin-bottom: 10px;
`
const SectionContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  align-content: flex-start;
  gap: 10px;
  width: 100%;
`
const SwitchDescription = styled.div`
  font-size: 15px;
  font-weight: 500;
  margin-left: 10px;
  width: 20%;
  width: 20%;
`

const AllowSalesContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  align-content: flex-start;
  gap: 30px;
  width: 100%;
`

export default ProductForm
